import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getShipmentDashboard } from '../config/api';
import './ShipmentDashboard.css';

const EMPTY_SUMMARY = { shipments: 0, orders: 0, pending: 0, started: 0, completed: 0 };
const EMPTY_DASHBOARD = {
  summary: EMPTY_SUMMARY,
  readiness: { assignedPending: 0, unassignedPending: 0, assignedDrivers: 0, activeDrivers: 0 },
  fleet: { averageUtilization: 0, twoWheelers: 0, fourWheelers: 0 },
  travel: { distanceMeters: 0, durationSeconds: 0, activeTrips: 0, completedTrips: 0, deliveryPartners: 0, includesActiveElapsedTime: false },
  partners: [],
  hubs: [],
  activity: [],
  filters: { hubs: [], drivers: [] },
};
const STATUS_LABELS = { pending: 'Pending', started: 'Ongoing', completed: 'Completed' };

const getIndiaDate = () => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date()).reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
};

const formatDate = value => new Date(`${value}T00:00:00`).toLocaleDateString('en-IN', {
  day: '2-digit', month: 'short', year: 'numeric',
});

const utilization = shipment => {
  return Number(shipment.capacityPoints) > 0
    ? Math.round((Number(shipment.loadPoints || 0) / Number(shipment.capacityPoints)) * 100)
    : 0;
};

const formatDistance = meters => `${(Number(meters || 0) / 1000).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;

const formatDuration = seconds => {
  const totalMinutes = Math.max(0, Math.round(Number(seconds || 0) / 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (!hours) return `${minutes} min`;
  return `${hours} hr${minutes ? ` ${minutes} min` : ''}`;
};

const ShipmentDashboard = () => {
  const [date, setDate] = useState(getIndiaDate);
  const [hubId, setHubId] = useState('all');
  const [driverId, setDriverId] = useState('all');
  const [dashboard, setDashboard] = useState(EMPTY_DASHBOARD);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getShipmentDashboard({ date, hubId, driverId })
      .then(response => {
        if (cancelled) return;
        setDashboard({ ...EMPTY_DASHBOARD, ...(response.data || {}) });
      })
      .catch(requestError => {
        if (!cancelled) setError(requestError.message || 'Could not load shipment dashboard');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [date, hubId, driverId]);

  const summary = dashboard.summary || EMPTY_SUMMARY;
  const insights = { ...(dashboard.readiness || EMPTY_DASHBOARD.readiness), ...(dashboard.fleet || EMPTY_DASHBOARD.fleet), hubs: dashboard.hubs || [] };
  const shipments = dashboard.activity || [];
  const hubs = dashboard.filters?.hubs || [];
  const drivers = dashboard.filters?.drivers || [];
  const travel = dashboard.travel || EMPTY_DASHBOARD.travel;
  const partners = dashboard.partners || [];

  const totalStatuses = Number(summary.pending || 0) + Number(summary.started || 0) + Number(summary.completed || 0);
  const statusPercent = status => totalStatuses ? Math.round((Number(summary[status] || 0) / totalStatuses) * 100) : 0;

  return <main className="shipment-dashboard-page">
    <header className="shipment-dashboard-header">
      <div><span className="shipment-dashboard-eyebrow">Delivery operations</span><h1>Shipment dashboard</h1><p>One view of dispatch readiness, drivers, fleet capacity and hub performance.</p></div>
      <div className="shipment-dashboard-header-actions">
        <Link className="btn btn-secondary" to="/shipments">View shipment list</Link>
      </div>
    </header>

    <section className="shipment-dashboard-filters" aria-label="Dashboard filters">
      <div><span>Viewing operations for</span><strong>{formatDate(date)}</strong></div>
      <label><span>Shipment date</span><input type="date" value={date} onChange={event => setDate(event.target.value)} /></label>
      <label><span>Hub</span><select value={hubId} onChange={event => { setHubId(event.target.value); setDriverId('all'); }}><option value="all">All accessible hubs</option>{hubs.map(hub => <option key={hub.id} value={hub.id}>{hub.name}</option>)}</select></label>
      <label><span>Delivery partner</span><select value={driverId} onChange={event => setDriverId(event.target.value)}><option value="all">All delivery partners</option>{drivers.map(driver => <option key={driver.id} value={driver.id}>{driver.name || driver.email}</option>)}</select></label>
    </section>

    {error && <div className="shipment-dashboard-error">{error}</div>}

    <section className="shipment-dashboard-kpis" aria-label="Shipment summary">
      <DashboardKpi label="Total shipments" value={summary.shipments} detail={`${summary.orders || 0} orders loaded`} tone="blue" loading={loading} />
      <DashboardKpi label="Ready to dispatch" value={summary.pending} detail={`${insights.unassignedPending} awaiting driver`} tone="amber" loading={loading} />
      <DashboardKpi label="On the road" value={summary.started} detail={`${insights.activeDrivers} active drivers`} tone="violet" loading={loading} />
      <DashboardKpi label="Completed" value={summary.completed} detail={`${statusPercent('completed')}% of selected-day trips`} tone="green" loading={loading} />
      <DashboardKpi label="Avg. utilization" value={`${insights.averageUtilization}%`} detail="Across planned vehicles" tone="teal" loading={loading} />
    </section>

    <section className="shipment-dashboard-grid">
      <article className="shipment-dashboard-card shipment-dashboard-status-card">
        <DashboardCardHeader title="Dispatch overview" subtitle="Current shipment progress" action={<Link to="/shipments">Manage shipments</Link>} />
        <div className="shipment-status-progress" aria-label="Shipment status distribution">
          {['pending', 'started', 'completed'].map(status => <div className={`is-${status}`} style={{ width: `${statusPercent(status)}%` }} key={status} />)}
        </div>
        <div className="shipment-status-legend">
          {['pending', 'started', 'completed'].map(status => <div key={status}><i className={`is-${status}`} /><span>{STATUS_LABELS[status]}</span><strong>{summary[status] || 0}</strong><small>{statusPercent(status)}%</small></div>)}
        </div>
        <div className="shipment-readiness-grid">
          <MiniStat label="Assigned and ready" value={insights.assignedPending} note="Pending shipments" />
          <MiniStat label="Needs driver" value={insights.unassignedPending} note="Action required" alert={insights.unassignedPending > 0} />
          <MiniStat label="Drivers allocated" value={insights.assignedDrivers} note="Across selected date" />
        </div>
      </article>

      <article className="shipment-dashboard-card shipment-dashboard-fleet-card">
        <DashboardCardHeader title="Fleet and capacity" subtitle="Planned vehicle distribution" />
        <div className="shipment-fleet-utilization"><div><span>Average load</span><strong>{insights.averageUtilization}%</strong></div><div className="shipment-fleet-meter"><i style={{ width: `${Math.min(insights.averageUtilization, 100)}%` }} /></div><small>{insights.averageUtilization > 100 ? 'Review overloaded vehicles' : 'Average planned capacity used'}</small></div>
        <div className="shipment-fleet-types">
          <div><span className="shipment-fleet-icon">4W</span><p><strong>{insights.fourWheelers}</strong><small>Four-wheelers</small></p></div>
          <div><span className="shipment-fleet-icon is-bike">2W</span><p><strong>{insights.twoWheelers}</strong><small>Two-wheelers</small></p></div>
        </div>
      </article>
    </section>

    <section className="shipment-dashboard-card shipment-dashboard-travel">
      <DashboardCardHeader title="Travel performance" subtitle={`Actual trip activity for ${formatDate(date)}`} />
      <div className="shipment-travel-layout">
        <div className="shipment-travel-summary">
          <TravelStat label="Distance travelled" value={formatDistance(travel.distanceMeters)} note={`${travel.completedTrips} completed ride${travel.completedTrips === 1 ? '' : 's'}`} tone="blue" loading={loading} />
          <TravelStat label="Travel time" value={formatDuration(travel.durationSeconds)} note={travel.includesActiveElapsedTime ? 'Includes ongoing ride time' : 'Completed ride time'} tone="violet" loading={loading} />
          <TravelStat label="Delivery partners" value={travel.deliveryPartners} note={`${travel.activeTrips} currently on the road`} tone="green" loading={loading} />
        </div>
        <div className="shipment-partner-table-wrap">
          <table>
            <thead><tr><th>Delivery partner</th><th>Shipments</th><th>Orders</th><th>Ongoing</th><th>Completed</th><th>Distance</th><th>Travel time</th></tr></thead>
            <tbody>
              {!loading && !partners.length && <tr><td colSpan="7" className="shipment-dashboard-empty">No delivery-partner travel is recorded for this selection.</td></tr>}
              {partners.map(partner => <tr key={partner.id}>
                <td><strong>{partner.name}</strong><span>{partner.email || 'Delivery access user'}</span></td>
                <td>{partner.shipments}</td><td>{partner.orders}</td><td>{partner.started}</td><td>{partner.completed}</td>
                <td><strong>{formatDistance(partner.distanceMeters)}</strong></td><td><strong>{formatDuration(partner.durationSeconds)}</strong></td>
              </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <section className="shipment-dashboard-card shipment-dashboard-hubs">
      <DashboardCardHeader title="Hub performance" subtitle="Dispatch activity by accessible hub" />
      <div className="shipment-hub-table-wrap"><table><thead><tr><th>Hub</th><th>Shipments</th><th>Orders</th><th>Assigned</th><th>Ongoing</th><th>Completed</th><th>Avg. load</th></tr></thead><tbody>
        {!loading && !insights.hubs.length && <tr><td colSpan="7" className="shipment-dashboard-empty">No shipments found for this date.</td></tr>}
        {insights.hubs.map(hub => <tr key={hub.id}><td><strong>{hub.name}</strong><span>{hub.pending} pending</span></td><td>{hub.shipments}</td><td>{hub.orders}</td><td>{hub.assigned}/{hub.shipments}</td><td>{hub.started}</td><td>{hub.completed}</td><td><div className="shipment-hub-load"><span><i style={{ width: `${Math.min(hub.averageUtilization, 100)}%` }} /></span><strong>{hub.averageUtilization}%</strong></div></td></tr>)}
      </tbody></table></div>
    </section>

    <section className="shipment-dashboard-card shipment-dashboard-recent">
      <DashboardCardHeader title="Shipment activity" subtitle="Latest shipments for the selected date" action={<Link to="/shipments">Open full list</Link>} />
      <div className="shipment-dashboard-activity-list">
        {!loading && !shipments.length && <div className="shipment-dashboard-empty">No shipment activity is available.</div>}
        {shipments.slice(0, 8).map(shipment => <Link to={`/shipments/${shipment.id}`} target="_blank" rel="noopener noreferrer" key={shipment.id}>
          <div><strong>{shipment.id}</strong><span>{shipment.hub} · {shipment.area || 'Area not available'}</span></div>
          <div className="shipment-activity-driver"><span>Driver</span><strong>{shipment.assignedTo?.name || 'Unassigned'}</strong></div>
          <div className="shipment-activity-load"><span>{utilization(shipment)}%</span><i><b style={{ width: `${Math.min(utilization(shipment), 100)}%` }} /></i></div>
          <span className={`shipment-dashboard-status is-${shipment.status}`}>{STATUS_LABELS[shipment.status]}</span>
          <b aria-hidden="true">→</b>
        </Link>)}
      </div>
    </section>
  </main>;
};

const DashboardKpi = ({ label, value, detail, tone, loading }) => <article className={`shipment-dashboard-kpi is-${tone}`}><span>{label}</span><strong>{loading ? '—' : value}</strong><small>{detail}</small></article>;
const DashboardCardHeader = ({ title, subtitle, action }) => <header className="shipment-dashboard-card-header"><div><h2>{title}</h2><p>{subtitle}</p></div>{action}</header>;
const MiniStat = ({ label, value, note, alert }) => <div className={alert ? 'is-alert' : ''}><span>{label}</span><strong>{value}</strong><small>{note}</small></div>;
const TravelStat = ({ label, value, note, tone, loading }) => <div className={`shipment-travel-stat is-${tone}`}><span>{label}</span><strong>{loading ? '—' : value}</strong><small>{note}</small></div>;

export default ShipmentDashboard;
